import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowLeftRight, FileDiff } from "lucide-react";
import { PageNumberWithComments } from "@/components/page-number-with-comments";
import { useProcessingStatus } from "@/hooks/use-processing-status";
import { ProcessingSkeleton } from "@/components/processing-skeleton";
import { HeatmapView } from "@/components/heatmap-view";
import type { Document, DocumentVersion, DocumentPage } from "@shared/schema";

interface CompareViewProps {
  document: Document;
  versions: DocumentVersion[];
  currentVersionId: string | null;
  onVersionChange: (versionId: string) => void;
}

interface CompareVersions {
  versionA: string | null;
  versionB: string | null;
}

type CompareSubMode = "side-by-side" | "diff-heatmap";



export function CompareView({ document, versions, currentVersionId, onVersionChange }: CompareViewProps) {
  const [, setLocation] = useLocation();
  const [compareSubMode, setCompareSubMode] = useState<CompareSubMode>("side-by-side");
  
  // Initialize with first two different versions 
  const [compareVersions, setCompareVersions] = useState<CompareVersions>(() => {
    const sortedVersions = [...versions].sort((a, b) => b.version - a.version); // Latest first
    const versionA = sortedVersions[1]?.id.toString() || sortedVersions[0]?.id.toString() || currentVersionId;
    const versionB = sortedVersions[0]?.id.toString() || currentVersionId;
    
    console.log('CompareView initialized:', { 
      versions: versions.map(v => `v${v.version}:${v.id}`), 
      versionA, 
      versionB,
      sortedVersions: sortedVersions.map(v => `v${v.version}:${v.id}`)
    });
    
    return { versionA, versionB };
  });

  const scrollRefA = useRef<HTMLDivElement>(null);
  const scrollRefB = useRef<HTMLDivElement>(null);

  // Find version objects first
  const versionA = versions.find(v => v.id.toString() === compareVersions.versionA);
  const versionB = versions.find(v => v.id.toString() === compareVersions.versionB);

  // Get pages for both versions using version NUMBERS, not IDs
  const { data: pagesDataA, isLoading: isLoadingPagesA } = useQuery({
    queryKey: ["/api/documents", document.id, "versions", versionA?.version, "pages"],
    queryFn: async () => {
      if (!versionA) return [];
      const response = await fetch(`/api/documents/${document.id}/versions/${versionA.version}/pages`);
      if (!response.ok) throw new Error('Failed to fetch version A pages');
      const data = await response.json();
      return data.pages || [];
    },
    enabled: !!versionA,
  });

  const { data: pagesDataB, isLoading: isLoadingPagesB, error: errorB } = useQuery({
    queryKey: ["/api/documents", document.id, "versions", versionB?.version, "pages"],
    queryFn: async () => {
      if (!versionB) return [];
      console.log(`Fetching version B pages: /api/documents/${document.id}/versions/${versionB.version}/pages`);
      const response = await fetch(`/api/documents/${document.id}/versions/${versionB.version}/pages`);
      console.log(`Version B response status: ${response.status}, ok: ${response.ok}`);
      if (!response.ok) {
        console.error(`Version B API error: ${response.status} ${response.statusText}`);
        throw new Error(`Failed to fetch version B pages: ${response.status}`);
      }
      const data = await response.json();
      console.log(`Version B pages data:`, data);
      return data.pages || [];
    },
    enabled: !!versionB,
  });

  // Ensure we always have arrays
  const pagesA = Array.isArray(pagesDataA) ? pagesDataA : [];
  const pagesB = Array.isArray(pagesDataB) ? pagesDataB : [];

  // Filter to only visible pages (matching filmstrip and browse mode logic)
  const visiblePagesA = pagesA.filter((p: DocumentPage) => !p.hidden);
  const visiblePagesB = pagesB.filter((p: DocumentPage) => !p.hidden);

  // Processing status for each version
  const processingStatusA = useProcessingStatus(
    !isLoadingPagesA && pagesA.length === 0 && versionA ? versionA.id : null
  );

  const processingStatusB = useProcessingStatus(
    !isLoadingPagesB && pagesB.length === 0 && versionB ? versionB.id : null
  );

  const isLoading = isLoadingPagesA || isLoadingPagesB;
  const maxPages = Math.max(visiblePagesA.length, visiblePagesB.length);
  
  console.log('CompareView pages:', { 
    pagesACount: pagesA.length, 
    pagesBCount: pagesB.length,
    compareVersions,
    versionA: versionA?.version,
    versionB: versionB?.version,
    errorB: errorB?.message,
    loadingA: isLoadingPagesA,
    loadingB: isLoadingPagesB
  });

  // Sync scroll between columns on desktop
  useEffect(() => {
    // Only set up sync for side-by-side mode
    if (compareSubMode !== "side-by-side") return;
    
    const scrollElA = scrollRefA.current;
    const scrollElB = scrollRefB.current;
    
    console.log('Setting up scroll sync', { scrollElA: !!scrollElA, scrollElB: !!scrollElB });
    
    if (!scrollElA || !scrollElB) return;

    let isScrollingA = false;
    let isScrollingB = false;

    const handleScrollA = () => {
      if (isScrollingB) return;
      isScrollingA = true;
      scrollElB.scrollTop = scrollElA.scrollTop;
      setTimeout(() => { isScrollingA = false; }, 10);
    };

    const handleScrollB = () => {
      if (isScrollingA) return;
      isScrollingB = true;
      scrollElA.scrollTop = scrollElB.scrollTop;
      setTimeout(() => { isScrollingB = false; }, 10);
    };

    scrollElA.addEventListener('scroll', handleScrollA, { passive: true });
    scrollElB.addEventListener('scroll', handleScrollB, { passive: true });

    console.log('Scroll sync listeners attached');

    return () => {
      scrollElA.removeEventListener('scroll', handleScrollA);
      scrollElB.removeEventListener('scroll', handleScrollB);
      console.log('Scroll sync listeners removed');
    };
  }, [compareSubMode, visiblePagesA.length, visiblePagesB.length]); // Re-run when pages load or mode changes

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="flex gap-4 justify-center">
            <div className="h-8 w-20 bg-muted rounded"></div>
            <div className="h-8 w-20 bg-muted rounded"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="w-full aspect-[8.5/11] bg-muted rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Version Selectors */}
      <div className="flex items-center justify-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">A:</span>
          <Select
            value={compareVersions.versionA || ""}
            onValueChange={(value) => {
              setCompareVersions(prev => ({ ...prev, versionA: value }));
              // Update URL with version numbers
              const newVersionA = versions.find(v => v.id.toString() === value);
              const currentVersionB = versions.find(v => v.id.toString() === compareVersions.versionB);
              if (newVersionA && currentVersionB) {
                setLocation(`/doc/${document.id}/compare/v/${newVersionA.version}/vs/${currentVersionB.version}`);
              }
            }}
          >
            <SelectTrigger className="w-20 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {versions.map((version) => (
                <SelectItem key={version.id} value={version.id.toString()}>
                  v{version.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">B:</span>
          <Select
            value={compareVersions.versionB || ""}
            onValueChange={(value) => {
              setCompareVersions(prev => ({ ...prev, versionB: value }));
              // Update URL with version numbers
              const newVersionB = versions.find(v => v.id.toString() === value);
              const currentVersionA = versions.find(v => v.id.toString() === compareVersions.versionA);
              if (currentVersionA && newVersionB) {
                setLocation(`/doc/${document.id}/compare/v/${currentVersionA.version}/vs/${newVersionB.version}`);
              }
            }}
          >
            <SelectTrigger className="w-20 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {versions.map((version) => (
                <SelectItem key={version.id} value={version.id.toString()}>
                  v{version.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sub-Mode Toggle */}
      <div className="flex items-center justify-center">
        <ToggleGroup 
          type="single" 
          value={compareSubMode} 
          onValueChange={(value) => value && setCompareSubMode(value as CompareSubMode)}
          className="border border-border rounded-lg"
        >
          <ToggleGroupItem value="side-by-side" className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" />
            A-B Compare
          </ToggleGroupItem>
          <ToggleGroupItem value="diff-heatmap" className="flex items-center gap-2">
            <FileDiff className="h-4 w-4" />
            Diff Heatmap
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {compareSubMode === "side-by-side" ? (
        <>
          {/* Desktop: Two Column Layout */}
      <div className="hidden md:grid md:grid-cols-2 gap-6">
        {/* Version A Column */}
        <div ref={scrollRefA} className="space-y-6 max-h-[80vh] overflow-y-auto pr-2">
          {processingStatusA.isProcessing ? (
            <ProcessingSkeleton 
              type="compare"
              progress={processingStatusA.progress}
              statusText={processingStatusA.statusText}
              documentColor={document.defaultIndicatorColor}
              aspectRatio="3/4"
            />
          ) : (
            Array.from({ length: maxPages }).map((_, pageIndex) => {
              const page = visiblePagesA[pageIndex];
              
              // If no page at this index, render empty space to maintain alignment
              if (!page) {
                return (
                  <div 
                    key={`empty-a-${pageIndex}`} 
                    className="w-full aspect-[3/4] opacity-0"
                  />
                );
              }
              
              return (
                <div 
                  key={page.id} 
                  className="relative cursor-pointer group"
                  onClick={() => {
                    if (versionA && !page.isPlaceholder) {
                      setLocation(`/doc/${document.id}/comment/v/${versionA.version}/p/${pageIndex + 1}`);
                    }
                  }}
                >
                  <PageNumberWithComments 
                    page={page} 
                    documentColor={document.defaultIndicatorColor}
                    versionLabel={`v${versionA?.version || ''} - A`}
                    pagePosition={pageIndex + 1}
                  />
                  {page.isPlaceholder ? (
                    <div 
                      className="w-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                      style={{
                        aspectRatio: `${page.width}/${page.height}`,
                        backgroundColor: document.defaultIndicatorColor + '20',
                        border: `2px dashed ${document.defaultIndicatorColor}60`,
                      }}
                    >
                      <div 
                        className="text-sm md:text-base lg:text-lg font-bold text-center leading-tight p-4"
                        style={{ color: document.defaultIndicatorColor }}
                        title={page.label || "New Slide"}
                      >
                        {page.label || "NEW SLIDE"}
                      </div>
                    </div>
                  ) : (
                    <img
                      src={page.webpUrl || ''}
                      alt={`Version A - Page ${pageIndex + 1}`}
                      className="w-full h-auto shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                      style={{ aspectRatio: `${page.width}/${page.height}` }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Version B Column */}
        <div ref={scrollRefB} className="space-y-6 max-h-[80vh] overflow-y-auto pr-2">
          {processingStatusB.isProcessing ? (
            <ProcessingSkeleton 
              type="compare"
              progress={processingStatusB.progress}
              statusText={processingStatusB.statusText}
              documentColor={document.defaultIndicatorColor}
              aspectRatio="3/4"
            />
          ) : (
            Array.from({ length: maxPages }).map((_, pageIndex) => {
              const page = visiblePagesB[pageIndex];
              
              // If no page at this index, render empty space to maintain alignment
              if (!page) {
                return (
                  <div 
                    key={`empty-b-${pageIndex}`} 
                    className="w-full aspect-[3/4] opacity-0"
                  />
                );
              }
              
              return (
                <div 
                  key={page.id} 
                  className="relative cursor-pointer group"
                  onClick={() => {
                    if (versionB && !page.isPlaceholder) {
                      setLocation(`/doc/${document.id}/comment/v/${versionB.version}/p/${pageIndex + 1}`);
                    }
                  }}
                >
                  <PageNumberWithComments 
                    page={page} 
                    documentColor={document.defaultIndicatorColor}
                    versionLabel={`v${versionB?.version || ''} - B`}
                    pagePosition={pageIndex + 1}
                  />
                  {page.isPlaceholder ? (
                    <div 
                      className="w-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                      style={{
                        aspectRatio: `${page.width}/${page.height}`,
                        backgroundColor: document.defaultIndicatorColor + '20',
                        border: `2px dashed ${document.defaultIndicatorColor}60`,
                      }}
                    >
                      <div 
                        className="text-sm md:text-base lg:text-lg font-bold text-center leading-tight p-4"
                        style={{ color: document.defaultIndicatorColor }}
                        title={page.label || "New Slide"}
                      >
                        {page.label || "NEW SLIDE"}
                      </div>
                    </div>
                  ) : (
                    <img
                      src={page.webpUrl || ''}
                      alt={`Version B - Page ${pageIndex + 1}`}
                      className="w-full h-auto shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                      style={{ aspectRatio: `${page.width}/${page.height}` }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Mobile: Alternating Single Column Layout (A-B-A-B) */}
      <div className="md:hidden w-full max-w-4xl mx-auto space-y-6">
        {/* Show processing skeletons if either version is processing */}
        {(processingStatusA.isProcessing || processingStatusB.isProcessing) ? (
          <div className="space-y-6">
            {processingStatusA.isProcessing && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">Version {versionA?.version} - A</h3>
                <ProcessingSkeleton 
                  type="canvas"
                  progress={processingStatusA.progress}
                  statusText={processingStatusA.statusText}
                  documentColor={document.defaultIndicatorColor}
                  aspectRatio="3/4"
                />
              </div>
            )}
            {processingStatusB.isProcessing && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">Version {versionB?.version} - B</h3>
                <ProcessingSkeleton 
                  type="canvas"
                  progress={processingStatusB.progress}
                  statusText={processingStatusB.statusText}
                  documentColor={document.defaultIndicatorColor}
                  aspectRatio="3/4"
                />
              </div>
            )}
          </div>
        ) : (
          Array.from({ length: maxPages }).map((_, pageIndex) => {
            const pageA = visiblePagesA[pageIndex];
            const pageB = visiblePagesB[pageIndex];
            
            return (
              <div key={pageIndex}>
                {/* Version A Page */}
                {pageA && (
                  <div 
                    className="relative mb-6 cursor-pointer group"
                    onClick={() => {
                      if (versionA && !pageA.isPlaceholder) {
                        setLocation(`/doc/${document.id}/comment/v/${versionA.version}/p/${pageIndex + 1}`);
                      }
                    }}
                  >
                    <PageNumberWithComments 
                      page={pageA} 
                      documentColor={document.defaultIndicatorColor}
                      versionLabel={`v${versionA?.version || ''} - A`}
                      pagePosition={pageIndex + 1}
                    />
                    {pageA.isPlaceholder ? (
                      <div 
                        className="w-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                        style={{
                          aspectRatio: `${pageA.width}/${pageA.height}`,
                          backgroundColor: document.defaultIndicatorColor + '20',
                          border: `2px dashed ${document.defaultIndicatorColor}60`,
                        }}
                      >
                        <div 
                          className="text-sm md:text-base lg:text-lg font-bold text-center leading-tight p-4"
                          style={{ color: document.defaultIndicatorColor }}
                          title={pageA.label || "New Slide"}
                        >
                          {pageA.label || "NEW SLIDE"}
                        </div>
                      </div>
                    ) : (
                      <img
                        src={pageA.webpUrl || ''}
                        alt={`Version A - Page ${pageIndex + 1}`}
                        className="w-full h-auto shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                        style={{ aspectRatio: `${pageA.width}/${pageA.height}` }}
                      />
                    )}
                  </div>
                )}
                
                {/* Version B Page */}
                {pageB && (
                  <div 
                    className="relative cursor-pointer group"
                    onClick={() => {
                      if (versionB && !pageB.isPlaceholder) {
                        setLocation(`/doc/${document.id}/comment/v/${versionB.version}/p/${pageIndex + 1}`);
                      }
                    }}
                  >
                    <PageNumberWithComments 
                      page={pageB} 
                      documentColor={document.defaultIndicatorColor}
                      versionLabel={`v${versionB?.version || ''} - B`}
                      pagePosition={pageIndex + 1}
                    />
                    {pageB.isPlaceholder ? (
                      <div 
                        className="w-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                        style={{
                          aspectRatio: `${pageB.width}/${pageB.height}`,
                          backgroundColor: document.defaultIndicatorColor + '20',
                          border: `2px dashed ${document.defaultIndicatorColor}60`,
                        }}
                      >
                        <div 
                          className="text-sm md:text-base lg:text-lg font-bold text-center leading-tight p-4"
                          style={{ color: document.defaultIndicatorColor }}
                          title={pageB.label || "New Slide"}
                        >
                          {pageB.label || "NEW SLIDE"}
                        </div>
                      </div>
                    ) : (
                      <img
                        src={pageB.webpUrl || ''}
                        alt={`Version B - Page ${pageIndex + 1}`}
                        className="w-full h-auto shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                        style={{ aspectRatio: `${pageB.width}/${pageB.height}` }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
        </>
      ) : (
        // Only render HeatmapView when both versions are available to prevent hooks ordering issues
        versionA && versionB ? (
          <HeatmapView 
            document={document}
            versionA={versionA}
            versionB={versionB}
            documentColor={document.defaultIndicatorColor}
          />
        ) : (
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">Loading versions...</div>
          </div>
        )
      )}
    </div>
  );
}